import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import StationManagement from './components/StationManagement';
import KioskOrders from './components/KioskOrders';
import Settings from './components/Settings';
import MobileOrder from './components/MobileOrder';
import { GameLoungeProvider } from './context/GameLoungeContext';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import './App.css';

const { FiDownload, FiX, FiSmartphone } = FiIcons;
const { FaGamepad } = FaIcons;

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Enhanced standalone detection
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://') ||
                      window.matchMedia('(display-mode: fullscreen)').matches ||
                      window.matchMedia('(display-mode: minimal-ui)').matches;
    
    setIsStandalone(standalone);

    // Handle install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install prompt only if not already installed
      if (!standalone) {
        // Check if user has dismissed it before
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) {
          // Show after 1 second delay for better UX
          setTimeout(() => setShowInstallPrompt(true), 1000);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA installed successfully');
      setShowInstallPrompt(false);
      localStorage.removeItem('pwa-install-dismissed');
      localStorage.setItem('pwa-installed', 'true');
      
      // Show success message
      setTimeout(() => {
        alert('🎉 App erfolgreich installiert! Du findest sie nun auf deinem Home-Bildschirm.');
      }, 500);
    });

    // Handle app update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 App update detected');
        const updateAccepted = confirm('🔄 Eine neue Version ist verfügbar. Jetzt aktualisieren?');
        if (updateAccepted) {
          window.location.reload();
        }
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('📱 iOS Installation: Drücke das Teilen-Symbol (⬆️) und wähle "Zum Home-Bildschirm"');
        setShowInstallPrompt(false);
        return;
      }
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ App installation accepted');
        setShowInstallPrompt(false);
        localStorage.removeItem('pwa-install-dismissed');
      } else {
        console.log('❌ App installation declined');
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Installation error:', error);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    // Remember dismissal for 7 days
    const dismissedUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-dismissed', dismissedUntil.toString());
  };

  // Re-check dismissal expiry
  useEffect(() => {
    if (!showInstallPrompt && deferredPrompt && !isStandalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (dismissed) {
        const dismissedUntil = parseInt(dismissed);
        if (Date.now() > dismissedUntil) {
          localStorage.removeItem('pwa-install-dismissed');
          setShowInstallPrompt(true);
        }
      }
    }
  }, [deferredPrompt, isStandalone, showInstallPrompt]);

  // Add to homescreen hint for iOS
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInstalled = localStorage.getItem('pwa-installed');
    
    if (isIOS && !isInstalled && !isStandalone) {
      const showIOSHint = () => {
        alert('📱 Installiere diese App: Drücke das Teilen-Symbol (⬆️) und wähle "Zum Home-Bildschirm"');
        localStorage.setItem('pwa-installed', 'true');
        document.removeEventListener('touchstart', showIOSHint);
      };
      
      // Show hint after first interaction
      document.addEventListener('touchstart', showIOSHint, { once: true });
    }
  }, [isStandalone]);

  return (
    <GameLoungeProvider>
      <Router>
        <div className="min-h-screen bg-gray-100">
          {/* Enhanced PWA Install Prompt - Centered Modal */}
          {showInstallPrompt && !isStandalone && (
            <>
              {/* Overlay backdrop with blur */}
              <div className="fixed inset-0 bg-black bg-opacity-60 z-40" style={{ backdropFilter: 'blur(4px)' }} />
              
              {/* Install Modal - Centered */}
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-sm w-full animate-fade-in">
                  {/* Header with gradient */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center">
                    <div className="flex justify-center mb-3">
                      <div className="bg-white bg-opacity-20 p-4 rounded-full animate-pulse">
                        <SafeIcon icon={FaGamepad} className="text-5xl" />
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Game Lounge Manager</h2>
                    <p className="text-blue-100 text-sm">Installiere die App für die beste Performance</p>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiSmartphone} className="text-2xl text-blue-600 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Schneller Zugriff</h3>
                        <p className="text-xs text-gray-600">Direkt vom Homescreen starten</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiDownload} className="text-2xl text-green-600 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Offline verfügbar</h3>
                        <p className="text-xs text-gray-600">Funktioniert auch ohne Internet</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FaGamepad} className="text-2xl text-purple-600 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-800 text-sm">Vollbild-Erlebnis</h3>
                        <p className="text-xs text-gray-600">Keine Browser-Ablenkungen</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-6 bg-gray-50 flex flex-col gap-3">
                    <button
                      onClick={handleInstallClick}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiDownload} className="text-xl" />
                      <span>Jetzt installieren</span>
                    </button>
                    <button
                      onClick={handleDismissInstall}
                      className="px-6 py-3 rounded-lg font-semibold text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiX} className="text-xl" />
                      <span>Später</span>
                    </button>
                  </div>

                  {/* Mobile hint */}
                  <div className="px-6 pb-4 bg-gray-50">
                    <p className="text-xs text-gray-500 text-center">
                      {/iPad|iPhone|iPod/.test(navigator.userAgent) ? 
                        'iOS: Teilen-Symbol → "Zum Home-Bildschirm"' : 
                        'Android: Installation startet automatisch'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          <Routes>
            <Route path="/mobile/:stationId" element={<MobileOrder />} />
            <Route
              path="/*"
              element={
                <div className="flex">
                  <Navigation />
                  <main className="flex-1 ml-64">
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/stations" element={<StationManagement />} />
                      <Route path="/kiosk" element={<KioskOrders />} />
                      <Route path="/settings" element={<Settings />} />
                    </Routes>
                  </main>
                </div>
              }
            />
          </Routes>
        </div>
      </Router>
    </GameLoungeProvider>
  );
}

export default App;